import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useAppFilters, useQueryNavigation } from '@/hooks';
import { HealthStatus, LAST_VERIFIED_TEST_ID } from '@/components/pages/AssetsPreviewPage';
import { format } from 'date-fns';
import { HEALTH_STATUS, REGULAR_DATE_FORMAT } from '@/const';
import { Highlight } from '@/components/uiKit/Highlight/Highlight';
import { Badge } from '@/components/ui/badge';
import { useParams } from 'react-router-dom';
import { navigateToAssetComponents } from '@/utils/shared/navigationToAssetComponents';
import { Fault } from '@/types/models/faults.types';
import { BadgeWithCounter } from '@/components/shared/BadgeWithCounter';
import { OverflowBadge } from '@/components/shared/OverflowBadge';

const healthStatusOrder: Record<string, number> = {
  [HEALTH_STATUS.CRITICAL]: 1,
  [HEALTH_STATUS.ALARM]: 2,
  [HEALTH_STATUS.MONITOR]: 3,
  [HEALTH_STATUS.HEALTHY]: 4,
  [HEALTH_STATUS.NOT_AVAILABLE]: 5,
};

export const useFaultsTable = () => {
  const { assetsType } = useAppFilters();
  const columns: ColumnDef<Fault>[] = useMemo(() => {
    return [
      {
        id: 'asset_ui_name',
        header: () => <div className="whitespace-nowrap">Asset Name</div>,
        accessorKey: 'asset_ui_name',
        accessorFn: (row) => `${row.location.asset_ui_name}`, // Combine both for search
        size: 30,
        enableSorting: true,
        enableSearch: true,
        enableGlobalSearch: true,
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true; // no search term, show all
          const searchValue = (filterValue as string).toLowerCase();
          const assetName = row.original.location.asset_ui_name?.toLowerCase() || '';
          return assetName.includes(searchValue);
        },
        cell: ({ row, table }) => (
          <>
            {(() => {
              const searchValue = (table.options.meta as any)?.searchValue || '';
              const { siteId } = useParams();
              const { navigateWithQuery } = useQueryNavigation();
              const onMachineItemClick = (e: any) => {
                e.stopPropagation();
                navigateToAssetComponents({
                  siteId: siteId!,
                  assetId: row.original.location.asset_id,
                  navigateWithQuery,
                });
              };
              return (
                <div className="text-sm font-semibold">
                  <span className="text-primary-600 cursor-pointer hover:underline" onClick={onMachineItemClick}>
                    <Highlight text={row.original.location.asset_ui_name} searchTerm={searchValue} />
                  </span>
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'component_name',
        header: () => <div className="whitespace-nowrap">Component</div>,
        accessorKey: 'component_name',
        accessorFn: (row) => `${row.location.component_ui_name}`, // Combine both for search
        size: 30,
        enableSorting: true,
        cell: ({ row, table }) => (
          <>
            {(() => {
              const { siteId } = useParams();
              const { navigateWithQuery } = useQueryNavigation();
              const onMachineItemClick = (e: any) => {
                e.stopPropagation();
                navigateToAssetComponents({
                  siteId: siteId!,
                  assetId: row.original.location.asset_id,
                  componentId: row.original.location.component_id,
                  navigateWithQuery,
                });
              };
              return (
                <div className="text-sm font-semibold">
                  <span className="text-primary-600 cursor-pointer hover:underline" onClick={onMachineItemClick}>
                    <Highlight text={row.original.location.component_ui_name} />
                  </span>
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'fault_type',
        header: () => <div className="whitespace-nowrap">Fault Name</div>,
        accessorKey: 'fault_type',
        accessorFn: (row) => `${row.fault_type.name}`, // Combine both for search
        size: 30,
        enableSorting: true,
        enableSearch: false,
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true; // no search term, show all
          const searchValue = (filterValue as string).toLowerCase();
          const assetName = row.original.fault_type.name?.toLowerCase() || '';
          return assetName.includes(searchValue);
        },
        cell: ({ row, table }) => (
          <>
            {(() => {
              const searchValue = (table.options.meta as any)?.searchValue || '';
              return (
                <div className="text-sm font-semibold">
                  <Highlight text={row.original.fault_type.name} searchTerm={searchValue} />
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'health_status',
        header: () => <div className="whitespace-nowrap">Severity</div>,
        accessorKey: 'health_status',
        size: 30,
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const a = healthStatusOrder[rowA.getValue(columnId) as string] ?? 999;
          const b = healthStatusOrder[rowB.getValue(columnId) as string] ?? 999;
          return a - b;
        },
        cell: ({ row }) => (
          <>
            {(() => {
              return <HealthStatus healthStatus={row.original.health_status} className="font-medium text-sm" />;
            })()}
          </>
        ),
      },
      {
        id: 'remarks',
        header: () => <div className="whitespace-nowrap">Remark</div>,
        accessorFn: (row) => row.evidence?.remarks,
        size: 40,
        enableSorting: false,
        meta: { hideWhenEmpty: true },
        cell: ({ row }) => {
          const remarks = row.original.evidence?.remarks;
          const sorted = remarks?.slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
          return <BadgeWithCounter items={sorted} />;
        },
      },
      {
        id: 'fault_status',
        header: () => <div className="whitespace-nowrap">Fault Status</div>,
        accessorKey: 'fault_status',
        accessorFn: (row) => (row.work_orders?.length ? 1 : 0),
        size: 30,
        enableSorting: true,
        enableSearch: false,
        cell: ({ row }) => (
          <>
            {(() => {
              return (
                <div className="text-sm font-semibold">
                  <Badge
                    variant="secondary"
                    className={` ${
                      row.original?.work_orders?.length
                        ? 'bg-[#E2E2FD] hover:bg-[#E2E2FD]'
                        : 'bg-neutral-50 hover:bg-neutral-50'
                    }`}
                  >
                    {row.original?.work_orders?.length ? 'WO Assigned' : 'WO not Assigned'}
                  </Badge>
                </div>
              );
            })()}
          </>
        ),
      },
      {
        id: 'asset_type',
        header: () => <div className="whitespace-nowrap">Asset Type</div>,
        accessorKey: 'asset_type',
        accessorFn: (row) => row.location.asset_type,
        size: 30,
        enableSorting: true,
        cell: ({ row }) => (
          <>
            {(() => {
              return row.original.location.asset_type;
            })()}
          </>
        ),
      },
      {
        id: 'asset_area',
        header: () => <div className="whitespace-nowrap">Asset Area</div>,
        accessorKey: 'asset_area',
        accessorFn: (row) => row.location.asset_area,
        size: 30,
        enableSorting: true,
        cell: ({ row }) => (
          <>
            {(() => {
              return row.original.location.asset_area;
            })()}
          </>
        ),
      },
      {
        id: 'days_open',
        header: () => <div className="whitespace-nowrap">Days Open</div>,
        accessorKey: 'days_open',
        accessorFn: (row) => row.timing?.days_open ?? -1,
        size: 30,
        enableSorting: true,
        cell: ({ row }) => {
          const daysOpen = row.original.timing?.days_open;
          return (
            <div className="text-sm font-medium">
              {daysOpen != null ? `${daysOpen} ${daysOpen === 1 ? 'day' : 'days'}` : 'N/A'}
            </div>
          );
        },
      },
      {
        id: 'work_orders',
        header: () => <div className="whitespace-nowrap">Assigned WOs</div>,
        accessorKey: 'work_orders',
        accessorFn: (row) => row.work_orders?.[0]?.wo_reference || '',
        size: 30,
        enableSorting: false,
        enableSearch: true,
        enableGlobalSearch: true,
        // meta: { allowCopy: true },
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const searchValue = (filterValue as string).toLowerCase();
          const workOrders = row.original.work_orders || [];
          return workOrders.some((wo) => wo.wo_reference?.toLowerCase().includes(searchValue));
        },
        cell: ({ row, table }) => {
          const workOrders = row.original.work_orders;
          const searchValue = (table.options.meta as any)?.searchValue || '';

          return <WorkOrdersBadge workOrders={workOrders} searchTerm={searchValue} />;
        },
      },
      {
        id: 'verification_date',
        header: () => <div className="whitespace-nowrap">Verified Date</div>,
        accessorKey: 'verification_date',
        size: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="font-medium text-sm text-gray-900" data-testid={LAST_VERIFIED_TEST_ID}>
            {(() => {
              const lastVerified = row.original.timing.validation_date; //currently we use the timing.validation_date until verification_date will have data to return
              return lastVerified ? format(new Date(lastVerified), REGULAR_DATE_FORMAT) : 'N/A';
            })()}
          </div>
        ),
      },
    ];
  }, [assetsType]);

  return { tableStructure: columns };
};

type WorkOrder = {
  wo_id: string;
  wo_reference: string;
};

type WorkOrdersBadgeProps = {
  workOrders: WorkOrder[];
  searchTerm?: string;
};

export function WorkOrdersBadge({ workOrders, searchTerm = '' }: WorkOrdersBadgeProps) {
  if (!workOrders || workOrders.length === 0) return null;

  const [first, ...rest] = workOrders;

  return (
    <div className="flex items-center gap-1 w-full min-w-0">
      <Highlight text={first.wo_reference} searchTerm={searchTerm} />
      <OverflowBadge
        count={rest.length}
        tooltipContent={
          <div className="flex flex-col gap-1">
            {rest.map((wo) => (
              <span key={wo.wo_reference} className="text-xs">
                {wo.wo_reference}
              </span>
            ))}
          </div>
        }
      />
    </div>
  );
}
